/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  atomOneLight,
  solarizedLight,
  vs2015,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import { FileType } from "../Api/Api";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { ThemeNames } from "../DesignSystem/Theme/Theme";

export function FileContentPreview({
  fileType,
  content,
}: {
  fileType: FileType;
  content: string;
}): JSX.Element {
  const { currentTheme, currentThemeName } = useDexterTheme();

  const syntaxStyle =
    currentThemeName === ThemeNames.DARK ||
    currentThemeName === ThemeNames.COLORBLIND_DARK
      ? vs2015
      : currentThemeName === ThemeNames.COLORBLIND_LIGHT
      ? solarizedLight
      : atomOneLight;

  // react-syntax-highlighter takes in a "style."  This
  // function returns a string for that style based on FileType.
  // As of 4/9/25, we are only working with proto and xml files.
  const getSyntaxHighlightType = (fileType: FileType): string => {
    switch (fileType) {
      case FileType.PROTO:
        return "protobuf";
      case FileType.XML:
        return "xml";
      default:
        return "plaintext";
    }
  };

  // We should only preview files that are in the FileType set
  // and are not UNDETECTED.  This takes care of the SyntaxHighlighter
  // trying to interpret the contents of a PDF in the file list.
  const supported = Object.values(FileType);
  const isPreviewable =
    fileType !== FileType.UNDETECTED && supported.includes(fileType);

  if (!isPreviewable) {
    return (
      <div
        style={{
          color: currentTheme.colors.text,
          fontFamily: currentTheme.fonts.primary,
          textAlign: "center",
          padding: currentTheme.spacing.md,
        }}
      >
        Preview not available for this file type.
      </div>
    );
  }

  return (
    <SyntaxHighlighter
      language={getSyntaxHighlightType(fileType)}
      style={syntaxStyle}
    >
      {content}
    </SyntaxHighlighter>
  );
}
